const SSO_SESSION_STATUS = {
  ACTIVE: 'active',
  MISSING: 'missing',
  INVALID: 'invalid',
  ERROR: 'error',
};

/**
 * @typedef {'active' | 'missing' | 'invalid' | 'error'} SSOSessionStatus
 */

/**
 * @typedef {Object} SSOSessionState
 * @property {SSOSessionStatus} status Состояние общей SSO-сессии:
 *   `active` - auth_adapter подтвердил наличие SSO-cookie;
 *   `missing` - SSO-cookie нет;
 *   `invalid` - SSO-cookie есть, но refresh/access пара больше не принимается;
 *   `error` - auth_adapter вернул ошибку или недоступен.
 * @property {boolean} canUseSessionForLogin True, если ответ позволяет попробовать вход через SSO-маркер.
 * @property {boolean} shouldReloadGuestSession True, если гостевая вкладка увидела SSO-сессию в другой вкладке.
 * @property {boolean} profileChanged True, если auth_adapter просит перечитать профиль текущего пользователя.
 * @property {string|number|null} ssoUserId ID пользователя в SSO, если auth_adapter вернул его в ответе.
 */

class AuthClient {
  /**
   * Проверяет endpoint `v0/sso/session`.
   *
   * Метод скрывает HTTP-детали auth_adapter от UI-кода и возвращает готовое
   * состояние для потока авторизации. `204` и `401` разделены специально:
   * отсутствие cookie можно открыть гостем, а невалидная пара токенов должна
   * пройти обычный SSO login redirect, который умеет выдать новые cookie.
   *
   * @returns {Promise<SSOSessionState>}
   */
  getSSOSession() {
    return fetch('api/v0/sso/session', {
      credentials: 'same-origin',
      cache: 'no-store',
    }).then(resp => {
      if (resp.status === 204) {
        return {
          status: SSO_SESSION_STATUS.MISSING,
          canUseSessionForLogin: false,
          shouldReloadGuestSession: false,
          profileChanged: false,
          ssoUserId: null,
        };
      }

      if (resp.status === 401) {
        return {
          status: SSO_SESSION_STATUS.INVALID,
          canUseSessionForLogin: false,
          shouldReloadGuestSession: false,
          profileChanged: false,
          ssoUserId: null,
        };
      }

      if (!resp.ok) {
        return {
          status: SSO_SESSION_STATUS.ERROR,
          canUseSessionForLogin: false,
          shouldReloadGuestSession: false,
          profileChanged: false,
          ssoUserId: null,
        };
      }

      return {
        status: SSO_SESSION_STATUS.ACTIVE,
        canUseSessionForLogin: true,
        shouldReloadGuestSession: resp.status === 200,
        profileChanged: resp.headers.get('X-Tinode-Profile-Changed') === '1',
        ssoUserId: resp.headers.get('sso_user_id') || null,
      };
    });
  }
}

export { AuthClient, SSO_SESSION_STATUS };
export default new AuthClient();
